/* eslint-disable max-len */
/**
 * @fileoverview Text Overlay Controls for video generation pipeline.
 *               Stage 5: Text overlay with AI-generated or manual text.
 *               Exports textElements for live CSS preview in VisualGenerator.
 *               PATH: src/components/contentHub/TextOverlayControls.js
 */

import React, {useState, useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../../firebaseConfig';
import {useToast} from '../shared/contexts/ToastContext';
import {FiType, FiZap, FiPlus, FiX, FiLoader, FiCheck, FiTrash2} from 'react-icons/fi';

const runVideoTextDesignerFn = httpsCallable(functions, 'runVideoTextDesigner', {timeout: 30000});
const applyVideoTextOverlayFn = httpsCallable(functions, 'applyVideoTextOverlay', {timeout: 120000});

// ─── Constants ──────────────────────────────────────────────────────────────

const TEXT_STRATEGIES = [
    {id: 'none', label: 'None', desc: 'No text overlay'},
    {id: 'smart', label: 'Smart', desc: 'AI decides text and timing'},
    {id: 'headline', label: 'Headline', desc: 'AI generates a hook'},
    {id: 'manual', label: 'Manual', desc: 'You write the text'},
];

const POSITION_OPTIONS = [
    {id: 'top', label: 'Top'},
    {id: 'center', label: 'Center'},
    {id: 'bottom', label: 'Bottom'},
];

const SIZE_OPTIONS = [
    {id: 'small', label: 'Small'},
    {id: 'medium', label: 'Medium'},
    {id: 'large', label: 'Large'},
];

const COLOR_PRESETS = [
    {id: '#FFFFFF', label: 'White'},
    {id: '#000000', label: 'Black'},
    {id: '#FFD700', label: 'Yellow'},
    {id: '#FF4444', label: 'Red'},
    {id: '#00E5FF', label: 'Cyan'},
];

const FONT_OPTIONS = [
    {id: 'inter', label: 'Clean'},
    {id: 'oswald', label: 'Impact'},
    {id: 'playfair', label: 'Elegant'},
];

const DEFAULT_ELEMENT = {
    text: '',
    position: 'top',
    startTime: 0,
    endTime: 5,
    fontSize: 'large',
    fontColor: '#FFFFFF',
    fontFamily: 'inter',
    outlineColor: '#000000',
    outlineWidth: 3,
    showBackground: false,
    bgColor: 'black@0.5',
};

// ─── Component ──────────────────────────────────────────────────────────────

function TextOverlayControls({project, post, platform, vibeSettings, onOverlayApplied, onPreviewChange, videoDuration = 5}) {
    const {addToast} = useToast();

    // Determine existing overlay state from post data OR local tracking
    const existingOverlay = post?.visual?.[platform]?.textOverlay;
    const hasExistingFromFirestore = Array.isArray(existingOverlay) && existingOverlay.length > 0;

    // Track if overlay has been applied in this session (handles local state gap)
    const [overlayAppliedLocally, setOverlayAppliedLocally] = useState(false);
    const hasActiveOverlay = hasExistingFromFirestore || overlayAppliedLocally;

    // State
    const [strategy, setStrategy] = useState(hasExistingFromFirestore ? 'manual' : 'none');
    const [textElements, setTextElements] = useState(hasExistingFromFirestore ? existingOverlay : []);
    const [isDesigning, setIsDesigning] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [expanded, setExpanded] = useState(hasExistingFromFirestore);

    // Send text elements to parent for live CSS preview — only when expanded and not none
    useEffect(() => {
        if (onPreviewChange) {
            onPreviewChange(expanded && strategy !== 'none' ? textElements : []);
        }
    }, [textElements, strategy, expanded, onPreviewChange]);

    // Reset text elements when strategy changes to none
    useEffect(() => {
        if (strategy === 'none') {
            setTextElements([]);
        }
    }, [strategy]);

    // ─── AI Text Designer ───────────────────────────────────────────────

    const handleDesignText = useCallback(async () => {
        setIsDesigning(true);
        try {
            const res = await runVideoTextDesignerFn({
                projectId: project.id,
                postId: post.id,
                platform,
                textStrategy: strategy,
                settings: vibeSettings || {},
            });
            if (res.data.success && res.data.textElements) {
                setTextElements(res.data.textElements);
                if (res.data.textElements.length === 0) {
                    addToast('AI recommends no text for this video.', 'info');
                } else {
                    addToast(`Generated ${res.data.textElements.length} text element(s).`, 'success');
                }
            }
        } catch (err) {
            addToast(err?.message || 'Text designer failed.', 'error');
        } finally {
            setIsDesigning(false);
        }
    }, [project.id, post.id, platform, strategy, vibeSettings, addToast]);

    // ─── Apply Overlay ──────────────────────────────────────────────────

    const handleApply = useCallback(async () => {
        const validElements = textElements.filter(el => el.text.trim().length > 0);
        if (validElements.length === 0) {
            addToast('Add at least one text element.', 'warning');
            return;
        }
        setIsApplying(true);
        try {
            const res = await applyVideoTextOverlayFn({
                projectId: project.id,
                postId: post.id,
                platform,
                textElements: validElements,
            });
            if (res.data.success) {
                setOverlayAppliedLocally(true);
                setExpanded(false); // Collapse panel to hide CSS mock (burned-in text is now on the video)
                addToast('Text overlay applied!', 'success');
                if (onOverlayApplied) onOverlayApplied(res.data.url, platform);
            }
        } catch (err) {
            addToast(err?.message || 'Text overlay failed.', 'error');
        } finally {
            setIsApplying(false);
        }
    }, [textElements, project.id, post.id, platform, addToast, onOverlayApplied]);

    // ─── Remove Overlay ─────────────────────────────────────────────────

    const handleRemove = useCallback(async () => {
        setIsRemoving(true);
        try {
            const res = await applyVideoTextOverlayFn({
                projectId: project.id,
                postId: post.id,
                platform,
                textElements: [],
            });
            if (res.data.success) {
                setTextElements([]);
                setStrategy('none');
                setOverlayAppliedLocally(false);
                addToast('Text overlay removed.', 'success');
                if (onOverlayApplied) onOverlayApplied(res.data.url, platform);
            }
        } catch (err) {
            addToast('Failed to remove overlay.', 'error');
        } finally {
            setIsRemoving(false);
        }
    }, [project.id, post.id, platform, addToast, onOverlayApplied]);

    // ─── Element Manipulation ───────────────────────────────────────────

    const addElement = useCallback(() => {
        if (textElements.length >= 3) return;
        const usedPositions = textElements.map(el => el.position);
        const available = ['top', 'center', 'bottom'].find(p => !usedPositions.includes(p)) || 'center';
        const idx = textElements.length;
        const defaultStart = idx === 0 ? 0 : idx === 1 ? Math.min(1.5, videoDuration - 2) : Math.min(3, videoDuration - 2);
        const defaultEnd = idx === 0 ? Math.min(3, videoDuration) : videoDuration;

        setTextElements(prev => [...prev, {
            ...DEFAULT_ELEMENT,
            position: available,
            startTime: defaultStart,
            endTime: defaultEnd,
        }]);
    }, [textElements, videoDuration]);

    const removeElement = useCallback((index) => {
        setTextElements(prev => prev.filter((_, i) => i !== index));
    }, []);

    const updateElement = useCallback((index, field, value) => {
        setTextElements(prev => prev.map((el, i) => i === index ? {...el, [field]: value} : el));
    }, []);

    const handleStrategyChange = useCallback((newStrategy) => {
        setStrategy(newStrategy);
        if (newStrategy === 'manual' && textElements.length === 0) {
            setTextElements([{...DEFAULT_ELEMENT}]);
        }
    }, [textElements.length]);

    // ─── Render ─────────────────────────────────────────────────────────

    const isAnyLoading = isDesigning || isApplying || isRemoving;

    return (
        <div className="rounded-xl border-2 border-white/10 p-4 transition-all bg-warm-gray/10">
            {/* Header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between"
            >
                <div className="flex items-center gap-2">
                    {hasActiveOverlay ? (
                        <FiCheck className="text-green-400" size={14} />
                    ) : (
                        <FiType className="text-cream/40" size={14} />
                    )}
                    <h4 className="text-xs font-black text-cream uppercase tracking-wider">Stage 5: Text Overlay</h4>
                </div>
                <span className="text-cream/30 text-[10px]">
                    {hasActiveOverlay ? 'Active' : 'Optional'}
                </span>
            </button>

            {expanded && (
                <div className="mt-4 space-y-4">
                    {/* Strategy Selector — bigger buttons */}
                    <div className="flex gap-2">
                        {TEXT_STRATEGIES.map(s => (
                            <button
                                key={s.id}
                                onClick={() => handleStrategyChange(s.id)}
                                disabled={isAnyLoading}
                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border ${
                                    strategy === s.id
                                        ? 'bg-slate-blue/30 border-slate-blue text-cream'
                                        : 'bg-warm-gray/10 border-white/5 text-cream/40 hover:text-cream/60 hover:border-white/10'
                                } disabled:opacity-40`}
                                title={s.desc}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>

                    {/* Smart/Headline: Generate Button */}
                    {(strategy === 'smart' || strategy === 'headline') && (
                        <button
                            onClick={handleDesignText}
                            disabled={isAnyLoading}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-warm-gray/20 hover:bg-warm-gray/30 text-cream/80 border border-white/10 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                        >
                            {isDesigning ? <FiLoader className="animate-spin" size={14} /> : <FiZap size={14} />}
                            {isDesigning ? 'Designing...' : `Generate ${strategy === 'headline' ? 'Headline' : 'Text'}`}
                        </button>
                    )}

                    {/* Text Elements Editor */}
                    {strategy !== 'none' && textElements.length > 0 && (
                        <div className="space-y-3">
                            {textElements.map((el, idx) => (
                                <div key={idx} className="rounded-lg border border-white/10 bg-warm-gray/10 p-3 space-y-3">
                                    {/* Header with remove */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-cream/60 font-bold uppercase">
                                            Text {idx + 1}
                                        </span>
                                        <button
                                            onClick={() => removeElement(idx)}
                                            className="text-cream/30 hover:text-red-400 transition-colors p-1"
                                            title="Remove element"
                                        >
                                            <FiX size={14} />
                                        </button>
                                    </div>

                                    {/* AI reasoning (collapsed) */}
                                    {el.reasoning && (
                                        <p className="text-[10px] text-cream/25 italic leading-tight">{el.reasoning}</p>
                                    )}

                                    {/* Text Input */}
                                    <input
                                        type="text"
                                        value={el.text}
                                        onChange={e => updateElement(idx, 'text', e.target.value.slice(0, 120))}
                                        placeholder="Enter overlay text..."
                                        className="w-full bg-warm-dark/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-cream placeholder-cream/20 focus:outline-none focus:border-slate-blue/50"
                                        maxLength={120}
                                    />

                                    {/* Controls Row 1: Position + Timing */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-cream/40 uppercase font-bold w-8">Pos</span>
                                            <div className="flex gap-1">
                                                {POSITION_OPTIONS.map(p => (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => updateElement(idx, 'position', p.id)}
                                                        className={`px-3 py-1.5 rounded text-[11px] font-bold transition-all ${
                                                            el.position === p.id
                                                                ? 'bg-slate-blue/30 text-cream border border-slate-blue/50'
                                                                : 'bg-warm-gray/10 text-cream/30 border border-white/5 hover:text-cream/50'
                                                        }`}
                                                    >
                                                        {p.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-cream/40 uppercase font-bold">Time</span>
                                            <input
                                                type="number"
                                                value={el.startTime}
                                                onChange={e => updateElement(idx, 'startTime', Math.max(0, Math.min(parseFloat(e.target.value) || 0, videoDuration)))}
                                                step="0.5"
                                                min="0"
                                                max={videoDuration}
                                                className="w-14 bg-warm-dark/50 border border-white/10 rounded px-2 py-1.5 text-xs text-cream text-center focus:outline-none focus:border-slate-blue/50"
                                            />
                                            <span className="text-xs text-cream/20">&rarr;</span>
                                            <input
                                                type="number"
                                                value={el.endTime}
                                                onChange={e => updateElement(idx, 'endTime', Math.max(0.5, Math.min(parseFloat(e.target.value) || videoDuration, videoDuration)))}
                                                step="0.5"
                                                min="0.5"
                                                max={videoDuration}
                                                className="w-14 bg-warm-dark/50 border border-white/10 rounded px-2 py-1.5 text-xs text-cream text-center focus:outline-none focus:border-slate-blue/50"
                                            />
                                            <span className="text-[10px] text-cream/20">sec</span>
                                        </div>
                                    </div>

                                    {/* Controls Row 2: Size + Color + Font + BG */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {/* Size */}
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-cream/40 uppercase font-bold">Size</span>
                                            <div className="flex gap-1">
                                                {SIZE_OPTIONS.map(s => (
                                                    <button
                                                        key={s.id}
                                                        onClick={() => updateElement(idx, 'fontSize', s.id)}
                                                        className={`px-2.5 py-1.5 rounded text-[11px] font-bold transition-all ${
                                                            el.fontSize === s.id
                                                                ? 'bg-slate-blue/30 text-cream border border-slate-blue/50'
                                                                : 'bg-warm-gray/10 text-cream/30 border border-white/5 hover:text-cream/50'
                                                        }`}
                                                    >
                                                        {s.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Color */}
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-cream/40 uppercase font-bold">Color</span>
                                            <div className="flex gap-1.5">
                                                {COLOR_PRESETS.map(c => (
                                                    <button
                                                        key={c.id}
                                                        onClick={() => updateElement(idx, 'fontColor', c.id)}
                                                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                                                            el.fontColor === c.id ? 'border-slate-blue scale-110 ring-1 ring-slate-blue/30' : 'border-white/20 hover:border-white/40'
                                                        }`}
                                                        style={{backgroundColor: c.id}}
                                                        title={c.label}
                                                    />
                                                ))}
                                                {!COLOR_PRESETS.some(c => c.id === el.fontColor) && (
                                                    <div
                                                        className="w-7 h-7 rounded-full border-2 border-slate-blue scale-110"
                                                        style={{backgroundColor: el.fontColor}}
                                                        title={`Vibe: ${el.fontColor}`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Controls Row 3: Font + Background */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-cream/40 uppercase font-bold">Font</span>
                                            <div className="flex gap-1">
                                                {FONT_OPTIONS.map(f => (
                                                    <button
                                                        key={f.id}
                                                        onClick={() => updateElement(idx, 'fontFamily', f.id)}
                                                        className={`px-3 py-1.5 rounded text-[11px] font-bold transition-all ${
                                                            el.fontFamily === f.id
                                                                ? 'bg-slate-blue/30 text-cream border border-slate-blue/50'
                                                                : 'bg-warm-gray/10 text-cream/30 border border-white/5 hover:text-cream/50'
                                                        }`}
                                                    >
                                                        {f.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => updateElement(idx, 'showBackground', !el.showBackground)}
                                            className={`px-3 py-1.5 rounded text-[11px] font-bold transition-all ${
                                                el.showBackground
                                                    ? 'bg-slate-blue/30 text-cream border border-slate-blue/50'
                                                    : 'bg-warm-gray/10 text-cream/30 border border-white/5 hover:text-cream/50'
                                            }`}
                                        >
                                            {el.showBackground ? 'BG Bar On' : 'BG Bar Off'}
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {/* Add Element Button */}
                            {textElements.length < 3 && (
                                <button
                                    onClick={addElement}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-warm-gray/10 hover:bg-warm-gray/20 text-cream/40 border border-dashed border-white/10 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors"
                                >
                                    <FiPlus size={14} /> Add Text Element
                                </button>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    {strategy !== 'none' && textElements.length > 0 && (
                        <button
                            onClick={handleApply}
                            disabled={isAnyLoading || textElements.every(el => !el.text.trim())}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-slate-blue hover:bg-slate-blue/80 text-cream border border-slate-blue rounded-xl text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {isApplying ? <FiLoader className="animate-spin" size={16} /> : <FiType size={16} />}
                            {isApplying ? 'Applying...' : 'Apply Text Overlay'}
                        </button>
                    )}

                    {/* Remove Overlay — visible when overlay was applied in this session OR exists in Firestore */}
                    {hasActiveOverlay && (
                        <button
                            onClick={handleRemove}
                            disabled={isAnyLoading}
                            className="w-full flex items-center justify-center gap-2 py-2.5 text-red-400/70 hover:text-red-400 border border-red-500/20 hover:border-red-500/40 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                        >
                            {isRemoving ? <FiLoader className="animate-spin" size={14} /> : <FiTrash2 size={14} />}
                            {isRemoving ? 'Removing...' : 'Remove Overlay'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

TextOverlayControls.propTypes = {
    project: PropTypes.object.isRequired,
    post: PropTypes.object.isRequired,
    platform: PropTypes.string.isRequired,
    vibeSettings: PropTypes.object,
    onOverlayApplied: PropTypes.func,
    onPreviewChange: PropTypes.func,
    videoDuration: PropTypes.number,
};

export default TextOverlayControls;
